/**
 * AINFRA-171 — reads and validates the runtime configuration that `/config.js` puts on
 * `window.__ARCHITEIA_CONFIG__` before the app bundle runs.
 */
import { ENVIRONMENTS, HOSTNAME_PATTERN, HTTPS_URL_PATTERN, HTTP_URL_PATTERN, ID_PATTERN, LOG_LEVELS, RUNTIME_CONFIG_FIELDS, RUNTIME_CONFIG_GLOBAL } from './schema.js';
/** Thrown when the runtime configuration is missing or invalid. `key` names the failing value. */
export class RuntimeConfigError extends Error {
    key;
    constructor(key, message) {
        super(`Runtime configuration: ${message}`);
        this.name = 'RuntimeConfigError';
        this.key = key;
    }
}
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isEnvironment(value) {
    return typeof value === 'string' && ENVIRONMENTS.includes(value);
}
function validateValue(field, value, environment) {
    const fail = (expected) => {
        throw new RuntimeConfigError(field.key, `${field.key} must be ${expected}`);
    };
    switch (field.kind) {
        case 'environment':
            if (!isEnvironment(value))
                fail(ENVIRONMENTS.join(' | '));
            return;
        case 'boolean':
            if (typeof value !== 'boolean')
                fail('a boolean');
            return;
        case 'logLevel':
            if (typeof value !== 'string' || !LOG_LEVELS.includes(value)) {
                fail(LOG_LEVELS.join(' | '));
            }
            return;
        case 'hostname':
            if (typeof value !== 'string' || !HOSTNAME_PATTERN.test(value))
                fail('a host name');
            return;
        case 'id':
            if (typeof value !== 'string' || !ID_PATTERN.test(value))
                fail('an identifier');
            return;
        case 'url': {
            const ok = typeof value === 'string' &&
                (HTTPS_URL_PATTERN.test(value) ||
                    (environment === 'development' && HTTP_URL_PATTERN.test(value)));
            if (!ok)
                fail(environment === 'development' ? 'an http(s) URL' : 'an https URL');
            return;
        }
    }
}
/**
 * Validates an unknown value against the fixed key list. Unknown keys are rejected (so no dev
 * switch can be smuggled in), `environment` is required, booleans default to `false` and every
 * other value is optional.
 */
export function parseRuntimeConfig(input) {
    if (!isPlainObject(input)) {
        throw new RuntimeConfigError(RUNTIME_CONFIG_GLOBAL, `${RUNTIME_CONFIG_GLOBAL} is not an object`);
    }
    const known = new Set(RUNTIME_CONFIG_FIELDS.map(f => f.key));
    for (const key of Object.keys(input)) {
        if (!known.has(key))
            throw new RuntimeConfigError(key, `unknown key ${key}`);
    }
    if (!isEnvironment(input.environment)) {
        throw new RuntimeConfigError('environment', `environment must be ${ENVIRONMENTS.join(' | ')}`);
    }
    const environment = input.environment;
    const result = {};
    for (const field of RUNTIME_CONFIG_FIELDS) {
        const value = input[field.key];
        if (value === undefined) {
            if (field.kind === 'boolean')
                result[field.key] = false;
            continue;
        }
        validateValue(field, value, environment);
        result[field.key] = value;
    }
    // Every key was checked against its field above; this is the one narrowing point.
    return result;
}
let cachedSource;
let cachedConfig;
function readGlobal() {
    return typeof window === 'undefined' ? undefined : window.__ARCHITEIA_CONFIG__;
}
function parseCached(source) {
    if (cachedConfig !== undefined && source === cachedSource)
        return cachedConfig;
    const config = parseRuntimeConfig(source);
    cachedSource = source;
    cachedConfig = config;
    return config;
}
/**
 * Shows the configuration error in place of the app. `config.js` is written by the container, so
 * this only appears on a misconfigured host; the text is deliberately technical and bilingual,
 * because it renders before the app (and its i18n catalogs) exist.
 */
export function showRuntimeConfigError(error) {
    if (typeof document === 'undefined' || document.body === null)
        return;
    const box = document.createElement('div');
    box.setAttribute('role', 'alert');
    box.setAttribute('data-runtime-config-error', error.key);
    box.style.cssText =
        'font-family:system-ui,sans-serif;max-width:40rem;margin:4rem auto;padding:0 1rem';
    const title = document.createElement('h1');
    title.textContent = 'This application is not configured correctly.';
    const titleDe = document.createElement('p');
    titleDe.textContent = 'Diese Anwendung ist nicht richtig konfiguriert.';
    const detail = document.createElement('pre');
    detail.textContent = error.message;
    box.append(title, titleDe, detail);
    document.body.replaceChildren(box);
}
/**
 * The app's runtime configuration. Throws {@link RuntimeConfigError} (after showing it on the page)
 * when `/config.js` did not load or holds an invalid value. Cached until the global changes.
 */
export function getRuntimeConfig() {
    const source = readGlobal();
    try {
        if (source === undefined) {
            throw new RuntimeConfigError(RUNTIME_CONFIG_GLOBAL, `${RUNTIME_CONFIG_GLOBAL} is not set; /config.js did not load`);
        }
        return parseCached(source);
    }
    catch (error) {
        if (error instanceof RuntimeConfigError)
            showRuntimeConfigError(error);
        throw error;
    }
}
/**
 * Like {@link getRuntimeConfig}, but returns `undefined` when no configuration is present (shared
 * libraries that also run in Node or in tests without a page). An invalid configuration still throws.
 */
export function findRuntimeConfig() {
    const source = readGlobal();
    return source === undefined ? undefined : parseCached(source);
}
